import { useEffect } from 'react';
import { Modal, Form, Button, ConfigProvider, theme } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import InterestBreakdownModal from './Components/InterestBreakdownModal';
import './datepicker-overrides.css';
import {
  setCurrentStep,
  toggleBreakdownModal,
  resetForm,
  updateFormData
} from '../../../redux/actions/investmentForm';
import {
  validateStep,
  submitInvestmentForm
} from '../../../redux/actions/investmentFormSubmission';
import {
  selectCurrentStep,
  selectCalculatedInterest,
  selectShowBreakdownModal,
  selectTransactionReceipt,
  selectFormData
} from '../../../redux/selectors/investmentFormSelectors';
import InvestmentModalFunderSelection from "./Components/InvestmentModalFunderSelection";
import InvestmentModalAmountDetails from "./Components/InvestmentModalAmountDetails";
import InvestmentModalDurationTerms from "./Components/InvestmentModalDurationTerms";

interface LogInvestmentModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  data: any;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  investRateId?: any;
  tenantId?: any;
  discardChanges?: (e: any) => void;
  allInvestmentProducts?: any[];
  state?: any;
  setState?: (state: any) => void;
  handleCurrency?: any;
}

type TState = {
  funder_id: number;
  cba_wallet_account_id: number;
  investment_product_id: number;
  cba_product_id: number;
  cba_product_interest_id: number;
  principal_amount: number;
  interest_rate: number;
  tenure: number;
  currency: string;
  interest_repayment_type: string;
  number_of_days_in_year: number;
  effective_date: string;
  transactionReceiptBase64: string;
  transactionReceiptName: string;
  amount_to_credit: number;
};



export const LogInvestmentModal = ({
  visible,
  onCancel,
  onSubmit,
  data,
  loading,
  setLoading,
  investRateId,
  tenantId,
  discardChanges,
  allInvestmentProducts = [],
  handleCurrency
}: LogInvestmentModalProps) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // Get all necessary state from Redux
  const currentStep = useSelector(selectCurrentStep);
  const calculatedInterest = useSelector(selectCalculatedInterest);
  const breakdownModalVisible = useSelector(selectShowBreakdownModal);
  const transactionReceipt = useSelector(selectTransactionReceipt);
  const formData = useSelector(selectFormData);

  // Reset form and state when modal closes
  useEffect(() => {
    if (!visible) {
      form.resetFields();
      dispatch(resetForm());
    }
  }, [visible, form, dispatch]);

  // Initialize walletLoading state when the modal is shown
  useEffect(() => {
    if (visible && currentStep === 0) {
      // Ensure walletLoading is initialized to false when the modal opens
      form.setFieldValue('walletLoading', false);
    }
  }, [visible, currentStep, form]);

  // Use form hook to get reactive walletLoading state
  const walletLoading = Form.useWatch('walletLoading', form);

  // Simplified navigation handlers using Redux actions
  const handleNext = async () => {
    if (currentStep === 1) {
      const principalAmount = Number(form.getFieldValue('principal_amount') || 0);
      if (principalAmount <= 0) {
        form.setFields([{
          name: 'principal_amount',
          errors: ['Investment amount must be greater than 0'],
        }]);
        return;
      }
    }
    // Check if wallet is loading before proceeding to the next step
    if (currentStep === 0 && walletLoading) {
      // Don't proceed if wallet is loading
      return;
    }

    await dispatch(validateStep(currentStep, form));
  };

  const handlePrevious = () => {
    dispatch(setCurrentStep(currentStep - 1));
  };


  const handleSubmit = async () => {
    if (currentStep === 2) {
    setLoading(true);
    const success = await dispatch(submitInvestmentForm(form, data, onSubmit));
    if (!success) {
      setLoading(false);
    }
    } else {
      handleNext();
    }
  };

  // Handler to close interest breakdown modal
  const hideInterestBreakdownModal = () => {
    dispatch(toggleBreakdownModal(false));
  };


  // Custom theme to fix date picker styling
  const customTheme = {
    algorithm: theme.defaultAlgorithm,
    token: {
      colorPrimary: '#713FFF',
      borderRadius: 8,
    },
    components: {
      DatePicker: {
        activeBorderColor: '#713FFF',
        hoverBorderColor: '#713FFF',
      },
    },
  };



  return (
    <>
      <Modal
        title={null}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={720}
        destroyOnClose={true}
        centered
        closable={true}
        closeIcon={<span style={{ fontSize: '24px' }}>×</span>}
        bodyStyle={{ padding: '40px' }}
      >
        <ConfigProvider theme={customTheme}>
          <Form
            form={form}
            layout="vertical"
            size="large"
            preserve={true}
          >
            {/* Use our new component structure while preserving the existing UI */}
            {currentStep === 0 && (
              <div className="steps-content">
                {/* Simplified Funder Selection component */}
                <InvestmentModalFunderSelection
                  form={form}
                  data={data}
                  allInvestmentProducts={allInvestmentProducts}
                  handleCurrency={handleCurrency}
                />
              </div>
            )}

            {currentStep === 1 && (
              <div className="steps-content">
                {/* Simplified Amount Details component */}
                <InvestmentModalAmountDetails
                  form={form}
                  data={data}
                />
              </div>
            )}

            {currentStep === 2 && (
              <div className="steps-content">
                {/* Simplified Duration Terms component */}
                <InvestmentModalDurationTerms
                  form={form}
                  tenantId={tenantId}
                  investRateId={investRateId}
                  data={data}
                />
              </div>
            )}

            <div className="steps-action mt-4" style={{
              display: 'flex',
              justifyContent: currentStep === 0 ? 'flex-end' : 'space-between',
              marginTop: '32px'
            }}>
              {currentStep > 0 && (
                <Button
                  style={{
                    minWidth: '120px',
                    height: '48px',
                    borderRadius: '6px',
                    border: '1px solid #713FFF',
                    color: '#713FFF',
                    fontSize: '16px',
                    fontWeight: 500
                  }}
                  onClick={handlePrevious}
                  disabled={loading}
                >
                  Previous
                </Button>
              )}

              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading || (currentStep === 0 && walletLoading)}
                disabled={loading || (currentStep === 0 && walletLoading)}
                style={{
                  minWidth: '120px',
                  height: '48px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: 500
                }}
              >
               {currentStep === 2 ? 'Submit' : 'Next'}
              </Button>
            </div>
          </Form>
        </ConfigProvider>
      </Modal>

      <InterestBreakdownModal
        visible={breakdownModalVisible}
        onCancel={hideInterestBreakdownModal}
        interestBreakdown={calculatedInterest.monthly_interest_accruals}
        currency={formData.currency}
      />
    </>
  );
};
